import moment from 'moment';

import { PushSchedule } from '../../../hooks/schedules';
import { activeLocaleTag, t } from '../../../utils/i18n/i18n';

// Chaves de tradução, resolvidas em tempo de render pelas funções abaixo — nunca no
// load do módulo, que roda antes de o idioma do tenant ser escolhido.
const RECURRENCE_LABEL: Record<string, string> = {
  NONE: 'Não repetir',
  WEEKLY: 'Toda semana',
  MONTHLY: 'Todo mês',
};

const TARGET_LABEL: Record<string, string> = {
  TENANT: 'Todos',
  USERS: 'Por Usuário',
  REGION: 'Por Região',
};

/**
 * Renderiza no fuso do PRÓPRIO agendamento e rotula com ele. Sem isso, um agendamento
 * colombiano (America/Bogota) visto do Brasil aparece 2h deslocado, sem nenhuma pista
 * de que a hora salva pelo operador era outra. moment-timezone não está instalado no
 * projeto, então quem converte é o Intl nativo — um timezone inválido levanta
 * RangeError, daí o fallback para o fuso do navegador.
 */
export const formatDateTime = (iso?: string | null, timezone?: string | null): string => {
  if (!iso) {
    return '—';
  }
  const locale = activeLocaleTag();
  try {
    if (timezone) {
      return `${new Date(iso).toLocaleString(locale, { timeZone: timezone })} (${timezone})`;
    }
    return new Date(iso).toLocaleString(locale);
  } catch {
    try {
      return new Date(iso).toLocaleString(locale);
    } catch {
      return String(iso);
    }
  }
};

export const formatRecurrence = (s: PushSchedule): string => {
  const base = t(RECURRENCE_LABEL[s.recurrence] || RECURRENCE_LABEL.NONE);
  if (s.recurrence === 'NONE' || !s.recurrenceUntil) {
    return base;
  }
  return `${base} ${t('até')} ${moment(s.recurrenceUntil).format('DD/MM/YYYY')}`;
};

/**
 * "usuários", nunca "dispositivos": a plataforma é 100% topic-based e não armazena
 * token FCM, então não sabe contar dispositivos (spec §3.3).
 */
export const formatAudience = (s: PushSchedule): string => {
  const parts: string[] = [
    TARGET_LABEL[s.targetType] ? t(TARGET_LABEL[s.targetType]) : s.targetType,
  ];

  if (s.targetType === 'REGION' && s.regionFilter) {
    const cities = s.regionFilter.cities || [];
    const states = s.regionFilter.states || [];
    if (cities.length > 0) {
      parts.push(cities.join(', '));
    } else if (states.length > 0) {
      parts.push(states.join(', '));
    }
  }

  if (typeof s.audienceEstimate === 'number') {
    parts.push(`${s.audienceEstimate.toLocaleString(activeLocaleTag())} ${t('usuários')}`);
  }

  return parts.join(' · ');
};
